import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  BookOpen,
  ChevronDown,
  Download,
  CircleDashed,
  FolderOpen,
  GitCompare,
  History,
  Lightbulb,
  PenLine,
  Plus,
  Search,
  Undo2,
  Upload,
  WrapText,
  X,
} from 'lucide-react';
import {
  BulkBar,
  FilterChip,
  FilterChips,
  GridIconButton,
  KeyHint,
  SearchField,
  Sheet,
  TreeRail,
  WorkspaceButton,
  WorkspaceDataGrid,
  WorkspaceHeader,
  WorkspaceLayout,
  WorkspaceMenu,
  isTypingTarget,
  useRegisterCommands,
  useToast,
  type PaletteCommand,
  type TreeRailCategory,
  type WorkspaceGridColumn,
  type WorkspaceMenuItem,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { Highlight } from '@/components/requirement-library/highlight';
import { cn } from '@/lib/utils/cn';
import { ApiError } from '@/lib/api/client';
import { useEngagementQuery } from '@/lib/api/engagements';
import { SelectionTabs, type SelectionTab } from './selection-tabs';
import {
  engagementRequirementKeys,
  useBulkRequirementsMutation,
  useCreateManualRequirementMutation,
  useEngagementRequirementsQuery,
  useSetRequirementStatusMutation,
  useSupportingDocumentsQuery,
  useUpdateRequirementMutation,
  type BulkResult,
  type Criticality,
  type EngagementRequirement,
  type LoadResult,
} from '@/lib/api/engagement-requirements';
import { useLibraryItemsQuery, useLibraryTreeQuery } from '@/lib/api/requirement-library';
import { useUsersLookupQuery } from '@/lib/api/users-lookup';
import {
  exportEngagementRequirements,
  useUndoEngagementImportMutation,
  type EngagementImportResult,
} from '@/lib/api/requirement-spreadsheets';
import { saveBlob } from '@/lib/utils/download';
import { CriticalityChip, CriticalityMenu, criticalityMenuItems } from './criticality';
import { EngagementImportSheet, ImportHistoryDialog } from './engagement-import';
import { LibraryMatchSheet } from './library-match/library-match-sheet';
import { LibraryPickerSheet } from './library-picker-sheet';
import {
  ManualAddPanel,
  duplicateFromError,
  type ManualAddModule,
  type ManualAddValues,
  type ManualDuplicate,
} from './manual-add-panel';
import { RequirementPanel } from './requirement-panel';
import { blankToNull } from '@/components/requirement-library/library-logic';
import {
  CRITICALITY_FILTERS,
  CRITICALITY_LABEL,
  GROUP_BY_OPTIONS,
  NOT_MAPPED_LABEL,
  NOT_MAPPED_VIEW,
  NO_CLIENT_AREA,
  REMOVED_VIEW,
  SOURCE_FILTERS,
  asCriticality,
  citationOf,
  citationSummary,
  computeView,
  criticalityUndoPlan,
  exportQuery,
  groupKeyOf,
  headerSummary,
  headerSummaryText,
  importResultMessage,
  libraryWordingDifference,
  loadResultMessage,
  moduleKeyOf,
  moduleLabelOf,
  moduleOrderOf,
  orderRows,
  plural,
  railCategories,
  undoImportMessage,
  type CriticalityFilter,
  type GroupBy,
  type SourceFilter,
} from './requirements-logic';
import { SignoffHeaderLink } from '@/components/discovery/signoff/signoff-header-link';

/**
 * Engagement -> Selection -> Requirements (T-0142 / E11-F04, with the
 * add-from-library picker of T-0143 / E11-F05).
 *
 * Design reference: the `#requirements` screen in
 * `docs/requirement-library/prototype/selection.js`. Built from the
 * shared workspace components in `components/ui/workspace` (amendment 01
 * Decision 28), like the requirement library screen.
 *
 * What the screen is for: tailoring one client's requirements. The
 * library is neutral (Decision 31), so criticality lives here and a
 * loaded requirement starts "Not set". Nobody types a number (Decision
 * 32). Every requirement shows its citation (Decision 34).
 *
 * Data: the whole engagement in one response; filtering, search, counts
 * and virtualised rendering are client-side so they are instant. Inline
 * edits save optimistically and say so when the server refuses. Bulk
 * changes are one request each and can be undone from the toast with the
 * inverse request.
 *
 * T-0153 adds the spreadsheet import (a sheet with the review of each
 * file, its history and Undo) and the export of all requirements or of
 * the current view to .xlsx.
 *
 * T-0264 adds "Match a spreadsheet to the library" (E14-F01): the client's
 * rows matched to library items, reviewed and applied in a sheet. After an
 * apply the list can show just the rows it added (URL `load`).
 *
 * Search text, the two filters and the selected module (or the Removed
 * view) live in the URL; the route owns them and passes them in, which
 * also keeps this component testable without a router.
 */

export interface EngagementRequirementsScreenProps {
  engagementId: string;
  /** Search text (URL `q`). */
  query: string;
  /** Selected module id or `removed` (URL `module`); null for all requirements. */
  view: string | null;
  /** URL `criticality`. */
  criticality: CriticalityFilter;
  /** URL `source`. */
  source: SourceFilter;
  onQueryChange: (query: string) => void;
  onViewChange: (view: string | null) => void;
  onCriticalityChange: (value: CriticalityFilter) => void;
  onSourceChange: (value: SourceFilter) => void;
  /** Clear the search and both filters in one step. */
  onClearFilters: () => void;
  /**
   * Open Add from discovery (T-0190), the fourth way to add requirements.
   * Offered only when the engagement's Discovery module is on.
   */
  onAddFromDiscovery?: () => void;
  /** URL `load`: only the rows one load brought in (T-0264), or null. */
  load?: string | null;
  /** Show one load's rows (resetting the other filters), or null for all again. */
  onLoadChange?: (load: string | null) => void;
  /** Open another Selection tab (T-0267: Vendor intelligence). */
  onSelectTab?: (tab: SelectionTab) => void;
}

const ALL_ID = '__all__';
const getRowId = (row: EngagementRequirement) => row.id;
const isActiveRow = (row: EngagementRequirement) => row.status !== 'removed';
/** The viewer's grouping choice, kept per browser (a convenience, never shared). */
const GROUP_BY_KEY = 'caliber.requirements.group_by';

function readGroupBy(): GroupBy {
  try {
    return window.localStorage.getItem(GROUP_BY_KEY) === 'client_area' ? 'client_area' : 'module';
  } catch {
    return 'module';
  }
}

/** Below this width the Citation column would squeeze the text to nothing. */
const CITATION_COLUMN_QUERY = '(min-width: 1281px)';

function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );
  const getSnapshot = React.useCallback(
    () =>
      typeof window === 'undefined' || !window.matchMedia ? true : window.matchMedia(query).matches,
    [query],
  );
  return React.useSyncExternalStore(subscribe, getSnapshot, () => true);
}

type PanelState = { kind: 'row'; id: string } | { kind: 'new' } | null;

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function EngagementRequirementsScreen({
  engagementId,
  query,
  view: viewParam,
  criticality,
  source,
  onQueryChange,
  onViewChange,
  onCriticalityChange,
  onSourceChange,
  onClearFilters,
  onAddFromDiscovery,
  load = null,
  onLoadChange,
  onSelectTab,
}: EngagementRequirementsScreenProps) {
  // `?module=` (empty) is "all requirements", exactly like no `module` at all.
  const view = blankToNull(viewParam);
  const qc = useQueryClient();
  const { toast } = useToast();
  const listQuery = useEngagementRequirementsQuery(engagementId);
  const moduleOn = !(listQuery.error instanceof ApiError && listQuery.error.code === 'not_found');
  const treeQuery = useLibraryTreeQuery();
  const itemsQuery = useLibraryItemsQuery();
  const engagementQuery = useEngagementQuery(engagementId);
  const tree = treeQuery.data;
  const engagementLabel = engagementQuery.data
    ? `${engagementQuery.data.client_name} · ${engagementQuery.data.name}`
    : null;
  const fromDiscovery =
    onAddFromDiscovery && engagementQuery.data?.modules_enabled.includes('discovery')
      ? onAddFromDiscovery
      : null;

  // ---- view state ---------------------------------------------------------
  const [wrap, setWrap] = React.useState(false);
  const [grouping, setGrouping] = React.useState<GroupBy>(readGroupBy);
  const changeGrouping = React.useCallback((next: GroupBy) => {
    setGrouping(next);
    try {
      window.localStorage.setItem(GROUP_BY_KEY, next);
    } catch {
      // Private window or blocked storage: the choice lasts for this visit.
    }
  }, []);
  const [focusedId, setFocusedId] = React.useState<string | null>(null);
  const [panel, setPanel] = React.useState<PanelState>(null);
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [matchOpen, setMatchOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [exportPerModule, setExportPerModule] = React.useState(false);
  const [exportRemoved, setExportRemoved] = React.useState(false);
  const [manualError, setManualError] = React.useState<string | null>(null);
  const [manualDuplicate, setManualDuplicate] = React.useState<ManualDuplicate | null>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const deferredQuery = React.useDeferredValue(query);

  // ---- data ---------------------------------------------------------------
  const moduleOrder = React.useMemo(() => moduleOrderOf(tree), [tree]);
  const allRows = React.useMemo(
    () => orderRows(listQuery.data?.items ?? [], moduleOrder, grouping),
    [listQuery.data, moduleOrder, grouping],
  );
  const v = React.useMemo(
    () => computeView(allRows, { query: deferredQuery, criticality, source, view, load }),
    [allRows, deferredQuery, criticality, source, view, load],
  );
  const rows = v.rows;
  const terms = v.terms;
  const removedView = view === REMOVED_VIEW;
  const summary = React.useMemo(() => headerSummary(v.live), [v.live]);

  const libraryById = React.useMemo(
    () => new Map((itemsQuery.data?.items ?? []).map((i) => [i.item_id, i])),
    [itemsQuery.data],
  );

  const rail = React.useMemo(
    () => railCategories(v.live, v.counts, tree),
    [v.live, v.counts, tree],
  );
  const railTree = React.useMemo<TreeRailCategory[]>(
    () =>
      rail.map((c) => ({
        id: c.id,
        label: c.name,
        count: c.count,
        items: c.modules.map((m) => ({ id: m.id, label: m.name, count: m.count })),
      })),
    [rail],
  );
  const moduleNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const r of allRows) map.set(moduleKeyOf(r), moduleLabelOf(r));
    return map;
  }, [allRows]);
  const viewModuleName =
    view === NOT_MAPPED_VIEW
      ? NOT_MAPPED_LABEL
      : view && !removedView
        ? (moduleNameById.get(view) ?? null)
        : null;
  /** Client area names as written, by group key, for the group headers. */
  const areaNameByKey = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const r of allRows) {
      const key = groupKeyOf(r, 'client_area');
      if (!map.has(key) && r.client_area) map.set(key, r.client_area);
    }
    return map;
  }, [allRows]);
  const notMappedLive = v.counts.get(NOT_MAPPED_VIEW) ?? 0;
  const hasNotMapped = v.live.some((r) => r.module_id === null);

  const panelRow = panel?.kind === 'row' ? (allRows.find((r) => r.id === panel.id) ?? null) : null;
  const panelCitation = panelRow ? citationOf(panelRow) : null;

  // Names the citation needs that the list does not carry.
  const lookupIds = React.useMemo(
    () => (panelRow && panelCitation?.kind === 'manual' ? [panelRow.created_by] : []),
    [panelRow, panelCitation],
  );
  const usersQuery = useUsersLookupQuery(lookupIds);
  const userName = React.useCallback(
    (id: string) => usersQuery.data?.items.find((u) => u.id === id)?.display_name ?? null,
    [usersQuery.data],
  );
  const documentsQuery = useSupportingDocumentsQuery(
    engagementId,
    moduleOn &&
      (panel?.kind === 'new' ||
        (panelCitation?.kind === 'manual' && panelCitation.supportingDocumentId != null)),
  );
  const documents = React.useMemo(
    () => (documentsQuery.data?.documents ?? []).map((d) => ({ id: d.id, name: d.name })),
    [documentsQuery.data],
  );
  const documentName = React.useCallback(
    (id: string) => documents.find((d) => d.id === id)?.name ?? null,
    [documents],
  );

  const manualModules = React.useMemo<ManualAddModule[]>(() => {
    const subs = new Map<string, Set<string>>();
    const add = (moduleId: string, sub: string) => {
      const set = subs.get(moduleId);
      if (set) set.add(sub);
      else subs.set(moduleId, new Set([sub]));
    };
    for (const i of itemsQuery.data?.items ?? []) add(i.module_id, i.subcategory);
    for (const r of allRows) if (r.module_id) add(r.module_id, r.subcategory);
    return (tree?.categories ?? []).flatMap((c) =>
      c.modules
        .filter((m) => m.status === 'active')
        .map((m) => ({
          id: m.id,
          name: m.name,
          prefix: m.prefix,
          subcategories: Array.from(subs.get(m.id) ?? []),
        })),
    );
  }, [tree, itemsQuery.data, allRows]);

  // ---- writes -------------------------------------------------------------
  const updateMutation = useUpdateRequirementMutation(engagementId);
  const statusMutation = useSetRequirementStatusMutation(engagementId);
  const bulkMutation = useBulkRequirementsMutation(engagementId);
  const createMutation = useCreateManualRequirementMutation(engagementId);
  const undoImportMutation = useUndoEngagementImportMutation(engagementId);
  const busy = statusMutation.isPending || bulkMutation.isPending;

  const saveRow = React.useCallback(
    (
      row: EngagementRequirement,
      change: {
        text?: string;
        criticality?: Criticality;
        client_area?: string | null;
        module_id?: string | null;
      },
    ) => {
      updateMutation.mutate(
        { id: row.id, ...change },
        {
          onError: (err) =>
            toast({
              message: `${row.number} was not saved: ${errorMessage(err, 'the change was refused.')}`,
            }),
        },
      );
    },
    // `mutate` is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast],
  );

  const commitText = React.useCallback(
    (row: EngagementRequirement, value: string) => {
      const text = value.trim();
      if (!text) {
        toast({ message: 'A requirement cannot be blank. Remove it instead.' });
        return;
      }
      if (text !== row.text) saveRow(row, { text });
    },
    [saveRow, toast],
  );

  const setCriticality = React.useCallback(
    (row: EngagementRequirement, value: Criticality) => {
      if (value !== row.criticality) saveRow(row, { criticality: value });
    },
    [saveRow],
  );

  const setClientArea = React.useCallback(
    (row: EngagementRequirement, value: string | null) => {
      if (value !== (row.client_area ?? null)) saveRow(row, { client_area: value });
    },
    [saveRow],
  );

  const setModule = React.useCallback(
    (row: EngagementRequirement, moduleId: string | null) => {
      if (moduleId !== row.module_id) saveRow(row, { module_id: moduleId });
    },
    [saveRow],
  );

  /**
   * Remove or restore rows. One row uses the item route, several use the
   * bulk route (one audited action). The toast offers Undo, which is the
   * inverse call on exactly the rows that changed.
   */
  const changeStatus = React.useCallback(
    (ids: string[], action: 'remove' | 'restore', undoable = true) => {
      if (ids.length === 0) return;
      const run: Promise<string[]> =
        ids.length === 1
          ? statusMutation.mutateAsync({ id: ids[0]!, action }).then(() => ids)
          : bulkMutation.mutateAsync({ kind: action, ids }).then((r: BulkResult) => r.ids);
      run.then(
        (changed) => {
          setSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.delete(id));
            return next;
          });
          if (action === 'remove') {
            setPanel((p) => (p?.kind === 'row' && ids.includes(p.id) ? null : p));
          }
          const n = plural(changed.length, 'requirement');
          toast({
            message:
              action === 'remove'
                ? `${n} removed. Find ${changed.length === 1 ? 'it' : 'them'} under Removed.`
                : `${n} restored.`,
            ...(undoable && changed.length > 0
              ? {
                  actions: [
                    {
                      label: 'Undo',
                      onClick: () =>
                        changeStatus(changed, action === 'remove' ? 'restore' : 'remove', false),
                    },
                  ],
                }
              : {}),
          });
        },
        (err: unknown) =>
          toast({
            message: errorMessage(
              err,
              action === 'remove'
                ? 'The requirements could not be removed.'
                : 'The requirements could not be restored.',
            ),
          }),
      );
    },
    // `mutateAsync` is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast],
  );

  const undoCriticality = React.useCallback(
    (previous: BulkResult['previous']) => {
      const plan = criticalityUndoPlan(previous);
      plan
        .reduce<Promise<unknown>>(
          (chain, step) =>
            chain.then(() =>
              bulkMutation.mutateAsync({
                kind: 'criticality',
                ids: step.ids,
                criticality: step.criticality,
              }),
            ),
          Promise.resolve(),
        )
        .then(
          () => toast({ message: 'Criticality change undone.' }),
          (err: unknown) =>
            toast({ message: errorMessage(err, 'The change could not be undone.') }),
        );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast],
  );

  const bulkSetCriticality = React.useCallback(
    (ids: string[], value: Criticality) => {
      if (ids.length === 0) return;
      bulkMutation.mutateAsync({ kind: 'criticality', ids, criticality: value }).then(
        (result) => {
          const label = CRITICALITY_LABEL[value];
          if (result.updated === 0) {
            toast({ message: `Already ${label}: nothing changed.` });
            return;
          }
          toast({
            message: `${plural(result.updated, 'requirement')} set to ${label}.`,
            actions: [{ label: 'Undo', onClick: () => undoCriticality(result.previous) }],
          });
        },
        (err: unknown) =>
          toast({ message: errorMessage(err, 'The criticality could not be changed.') }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, undoCriticality],
  );

  const addManually = (values: ManualAddValues) => {
    setManualError(null);
    setManualDuplicate(null);
    createMutation.mutate(values, {
      onSuccess: (row) => {
        onClearFilters();
        onViewChange(null);
        setFocusedId(row.id);
        setPanel({ kind: 'row', id: row.id });
        toast({ message: `${row.number} added.` });
      },
      onError: (err) => {
        // The same wording is already here: the panel asks before adding it again.
        const duplicate = duplicateFromError(err, values.text);
        if (duplicate) setManualDuplicate(duplicate);
        else setManualError(errorMessage(err, 'The requirement could not be added.'));
      },
    });
  };

  const onLoaded = (result: LoadResult) => {
    setPickerOpen(false);
    onClearFilters();
    onViewChange(null);
    setSelected(new Set());
    toast({ message: loadResultMessage(result) });
  };

  /** Undo one import: its rows are removed, except those edited since. */
  const undoImport = React.useCallback(
    (loadId: string) => {
      undoImportMutation.mutateAsync(loadId).then(
        (result) => toast({ message: undoImportMessage(result), durationMs: 12_000 }),
        (err: unknown) => toast({ message: errorMessage(err, 'The import could not be undone.') }),
      );
    },
    // `mutateAsync` is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast],
  );

  const onImported = (result: EngagementImportResult) => {
    onClearFilters();
    onViewChange(null);
    setSelected(new Set());
    toast({
      message: importResultMessage(result),
      actions: [{ label: 'Undo', onClick: () => undoImport(result.load_id) }],
      durationMs: 12_000,
    });
  };

  /**
   * Download the requirements as .xlsx: all of them, or what the table
   * shows now (the selected module or Removed view, both filters, search).
   */
  const runExport = React.useCallback(
    (scope: 'all' | 'view') => {
      if (exporting) return;
      setExporting(true);
      exportEngagementRequirements(
        engagementId,
        exportQuery(scope === 'view' ? { query, criticality, source, view, load } : null, {
          perModule: exportPerModule,
          includeRemoved: exportRemoved,
        }),
      )
        .then(
          (file) => {
            saveBlob(file.fileName, file.blob);
            toast({
              message: `${file.fileName} downloaded${
                file.rowCount != null ? ` with ${plural(file.rowCount, 'requirement')}` : ''
              }.`,
            });
          },
          (err: unknown) =>
            toast({ message: `Export failed: ${errorMessage(err, 'the file was not produced.')}` }),
        )
        .finally(() => setExporting(false));
    },
    [
      engagementId,
      exporting,
      query,
      criticality,
      source,
      view,
      load,
      exportPerModule,
      exportRemoved,
      toast,
    ],
  );

  // ---- navigation helpers -------------------------------------------------
  const selectView = React.useCallback(
    (next: string | null) => {
      setPanel((p) => (p?.kind === 'new' ? p : null));
      setFocusedId(null);
      setSelected(new Set());
      onViewChange(next);
    },
    [onViewChange],
  );

  const openPicker = React.useCallback(() => setPickerOpen(true), []);
  const openImport = React.useCallback(() => setImportOpen(true), []);
  const openMatch = React.useCallback(() => setMatchOpen(true), []);
  const openManual = React.useCallback(() => {
    setManualError(null);
    setManualDuplicate(null);
    setPanel({ kind: 'new' });
  }, []);

  // Leave the Removed view once nothing is removed any more.
  const removedCount = v.removed.length;
  React.useEffect(() => {
    if (removedView && listQuery.data && removedCount === 0) onViewChange(null);
  }, [removedView, removedCount, listQuery.data, onViewChange]);

  // "/" focuses search, as the footer hint says.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ---- command palette ----------------------------------------------------
  const hasRows = allRows.length > 0;
  const staticCommands = React.useMemo<PaletteCommand[]>(() => {
    if (!moduleOn) return [];
    const list: PaletteCommand[] = [
      {
        id: 'engagement-requirements:add-from-library',
        group: 'Requirements',
        label: 'Add requirements from the library',
        keywords: ['load', 'library', 'picker', 'add'],
        icon: <BookOpen className="h-3.5 w-3.5" />,
        run: openPicker,
      },
      {
        id: 'engagement-requirements:add-manual',
        group: 'Requirements',
        label: 'Write a requirement manually',
        keywords: ['add', 'new', 'manual'],
        icon: <PenLine className="h-3.5 w-3.5" />,
        run: openManual,
      },
      {
        id: 'engagement-requirements:import-spreadsheet',
        group: 'Requirements',
        label: 'Import a spreadsheet',
        keywords: ['import', 'upload', 'xlsx', 'csv', 'excel', 'add'],
        icon: <Upload className="h-3.5 w-3.5" />,
        run: openImport,
      },
      {
        id: 'engagement-requirements:match-spreadsheet',
        group: 'Requirements',
        label: 'Match a spreadsheet to the library',
        keywords: ['match', 'library', 'rtm', 'spreadsheet', 'xlsx', 'upload', 'add'],
        icon: <GitCompare className="h-3.5 w-3.5" />,
        run: openMatch,
      },
      {
        id: 'engagement-requirements:import-history',
        group: 'Requirements',
        label: 'Show spreadsheet imports',
        keywords: ['imports', 'history', 'undo import'],
        icon: <History className="h-3.5 w-3.5" />,
        run: () => setHistoryOpen(true),
      },
    ];
    if (hasRows) {
      list.push({
        id: 'engagement-requirements:export',
        group: 'Requirements',
        label: 'Export requirements to a spreadsheet',
        keywords: ['export', 'download', 'xlsx', 'excel'],
        icon: <Download className="h-3.5 w-3.5" />,
        run: () => runExport('all'),
      });
    }
    for (const c of rail) {
      for (const m of c.modules) {
        list.push({
          id: `engagement-requirements:module:${m.id}`,
          group: 'Requirement modules in this engagement',
          label: m.name,
          keywords: [c.name, 'go to module', 'jump'],
          icon: <FolderOpen className="h-3.5 w-3.5" />,
          hint: plural(m.count, 'requirement'),
          run: () => selectView(m.id),
        });
      }
    }
    return list;
  }, [moduleOn, rail, hasRows, openPicker, openManual, openImport, runExport, selectView]);
  useRegisterCommands(staticCommands);

  const findCommands = React.useCallback(
    (q: string): PaletteCommand[] => {
      const needle = q.trim().toLowerCase();
      if (!needle) return [];
      const found: PaletteCommand[] = [];
      for (const row of allRows) {
        if (!row.number.toLowerCase().includes(needle)) continue;
        found.push({
          id: `engagement-requirements:row:${row.id}`,
          group: 'Requirements in this engagement',
          mono: row.number,
          label: row.text,
          hint: row.status === 'removed' ? `${moduleLabelOf(row)} · removed` : moduleLabelOf(row),
          run: () => {
            onClearFilters();
            onViewChange(row.status === 'removed' ? REMOVED_VIEW : moduleKeyOf(row));
            setSelected(new Set());
            setFocusedId(row.id);
            setPanel({ kind: 'row', id: row.id });
          },
        });
        if (found.length >= 8) break;
      }
      return found;
    },
    [allRows, onClearFilters, onViewChange],
  );
  useRegisterCommands(findCommands);

  // ---- grid ---------------------------------------------------------------
  const panelOpen = panel != null;
  const roomForCitation = useMediaQuery(CITATION_COLUMN_QUERY);
  const columns = React.useMemo<WorkspaceGridColumn<EngagementRequirement>[]>(() => {
    const cols: WorkspaceGridColumn<EngagementRequirement>[] = [
      {
        key: 'number',
        header: 'No.',
        width: '88px',
        cell: (r) => (
          <span className="font-mono text-meta font-medium text-ink-700">
            <Highlight text={r.number} terms={terms} />
          </span>
        ),
      },
      {
        key: 'text',
        header: 'Requirement',
        editValue: (r) => r.text,
        editLabel: 'Requirement text',
        cell: (r) => (
          <span
            className="text-ink-900"
            title={r.status === 'removed' ? undefined : 'Double-click to edit'}
          >
            <Highlight text={r.text} terms={terms} />
          </span>
        ),
      },
      {
        key: 'criticality',
        header: 'Criticality',
        width: '140px',
        text: false,
        cell: (r) =>
          r.status === 'removed' ? (
            <CriticalityChip value={asCriticality(r.criticality)} />
          ) : (
            <CriticalityMenu
              value={asCriticality(r.criticality)}
              number={r.number}
              onChange={(value) => setCriticality(r, value)}
            />
          ),
      },
    ];
    // The panel repeats the citation in full, so the column gives its
    // width back to the text while the panel is open, and on a narrow
    // window where the text would otherwise have no room.
    if (!panelOpen && roomForCitation) {
      cols.push({
        key: 'citation',
        header: 'Citation',
        width: '230px',
        text: false,
        cell: (r) => {
          const c = citationSummary(r);
          return (
            <span className="flex min-w-0 items-center gap-1.5 text-meta text-ink-500">
              <span className="inline-flex h-5 flex-none items-center rounded-full bg-ink-100 px-2 text-[11px] font-medium text-ink-700">
                {c.label}
              </span>
              <span className="min-w-0 truncate">{c.detail}</span>
              {r.is_edited ? (
                <span className="flex-none text-[11px] font-medium text-accent-amber-700">
                  Edited
                </span>
              ) : null}
            </span>
          );
        },
      });
    }
    return cols;
  }, [terms, panelOpen, roomForCitation, setCriticality]);

  const showModuleInGroup = view == null || removedView;
  const groupBy = React.useCallback(
    (row: EngagementRequirement) => groupKeyOf(row, grouping),
    [grouping],
  );
  const renderGroup = React.useCallback(
    (key: string, count: number) => {
      const [head = '', sub = ''] = key.split('\u0000');
      if (head === 'area' && grouping === 'client_area') {
        const name = sub ? (areaNameByKey.get(key) ?? sub) : NO_CLIENT_AREA;
        return (
          <>
            <span className={cn('truncate', !sub && 'font-normal text-ink-500')}>
              <Highlight text={name} terms={terms} />
            </span>
            <span className="ml-2 font-mono font-normal text-ink-500">{count}</span>
          </>
        );
      }
      return (
        <>
          {showModuleInGroup ? (
            <span className="mr-1 truncate font-normal text-ink-500">
              {moduleNameById.get(head) ?? ''} ›
            </span>
          ) : null}
          <span className="truncate">
            <Highlight text={sub} terms={terms} />
          </span>
          <span className="ml-2 font-mono font-normal text-ink-500">{count}</span>
        </>
      );
    },
    [grouping, areaNameByKey, showModuleInGroup, moduleNameById, terms],
  );

  const rowActions = React.useCallback(
    (r: EngagementRequirement) =>
      r.status === 'removed' ? (
        <GridIconButton
          aria-label={`Restore ${r.number}`}
          title="Restore"
          onClick={() => changeStatus([r.id], 'restore')}
        >
          <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
        </GridIconButton>
      ) : (
        <GridIconButton
          aria-label={`Remove ${r.number}`}
          title="Remove"
          onClick={() => changeStatus([r.id], 'remove')}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </GridIconButton>
      ),
    [changeStatus],
  );

  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;
  const onEditCommit = React.useCallback(
    (rowId: string, _columnKey: string, value: string) => {
      const row = rowsRef.current.find((r) => r.id === rowId);
      if (row) commitText(row, value);
    },
    [commitText],
  );
  const openRow = React.useCallback((id: string) => setPanel({ kind: 'row', id }), []);
  const focusRow = React.useCallback((id: string) => {
    setFocusedId(id);
    // Like the prototype: once a requirement is open the panel follows focus.
    setPanel((p) => (p?.kind === 'row' ? { kind: 'row', id } : p));
  }, []);

  // ---- states before the workspace can render -----------------------------
  if (listQuery.error) {
    if (!moduleOn) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
          <h1 className="text-h2 font-semibold text-ink-900">Selection is not available here</h1>
          <p className="max-w-md text-body-sm text-ink-500">
            The Selection module is not enabled for this engagement, or you do not have access to
            it. Requirements live in engagements that have Selection turned on.
          </p>
        </div>
      );
    }
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <ErrorMessage error={listQuery.error} />
        <WorkspaceButton onClick={() => void listQuery.refetch()}>Try again</WorkspaceButton>
      </div>
    );
  }
  if (!listQuery.data) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading the requirements…
      </div>
    );
  }

  // ---- header -------------------------------------------------------------
  const addMenuItems: WorkspaceMenuItem[] = [
    {
      id: 'library',
      label: 'From the library',
      icon: <BookOpen className="h-3.5 w-3.5" />,
      onSelect: openPicker,
    },
    {
      id: 'manual',
      label: 'Write one manually',
      icon: <PenLine className="h-3.5 w-3.5" />,
      onSelect: openManual,
    },
    {
      id: 'import',
      label: 'Import a spreadsheet',
      icon: <Upload className="h-3.5 w-3.5" />,
      onSelect: openImport,
    },
    {
      id: 'match',
      label: 'Match a spreadsheet to the library',
      icon: <GitCompare className="h-3.5 w-3.5" />,
      hint: "Finds the library item for each row; what doesn't match waits for you.",
      onSelect: openMatch,
    },
    ...(fromDiscovery
      ? [
          {
            id: 'discovery',
            label: 'From discovery',
            icon: <Lightbulb className="h-3.5 w-3.5" />,
            hint: 'Suggested from what people said',
            onSelect: fromDiscovery,
          },
        ]
      : []),
    {
      id: 'imports',
      label: 'Imports',
      icon: <History className="h-3.5 w-3.5" />,
      hint: 'History and undo',
      separatorBefore: true,
      onSelect: () => setHistoryOpen(true),
    },
  ];

  const exportAllCount = exportRemoved ? allRows.length : v.live.length;
  const exportMenuItems: WorkspaceMenuItem[] = [
    {
      id: 'all',
      label: 'All requirements',
      hint: exportAllCount.toLocaleString(),
      onSelect: () => runExport('all'),
    },
    {
      id: 'view',
      label: 'Current view',
      hint: rows.length.toLocaleString(),
      onSelect: () => runExport('view'),
    },
    {
      id: 'per-module',
      label: 'One sheet per module',
      checked: exportPerModule,
      separatorBefore: true,
      onSelect: () => setExportPerModule((on) => !on),
    },
    {
      id: 'include-removed',
      label: 'Include removed',
      checked: exportRemoved,
      onSelect: () => setExportRemoved((on) => !on),
    },
  ];

  const hasAny = allRows.length > 0;
  const header = (
    <>
      <WorkspaceHeader
        title="Requirements"
        meta={`${engagementLabel ? `${engagementLabel} · ` : ''}${headerSummaryText(summary)}`}
        actions={
          hasAny ? (
            <>
              <SignoffHeaderLink engagementId={engagementId} />
              <WorkspaceMenu
                aria-label="Export requirements"
                label="Export to .xlsx"
                align="end"
                items={exportMenuItems}
                trigger={
                  <WorkspaceButton size="md" disabled={exporting}>
                    <Download className="h-3.5 w-3.5" aria-hidden="true" />
                    {exporting ? 'Exporting…' : 'Export'}
                    <ChevronDown className="h-3 w-3" aria-hidden="true" />
                  </WorkspaceButton>
                }
              />
              <WorkspaceMenu
                aria-label="Add requirements"
                align="end"
                items={addMenuItems}
                trigger={
                  <WorkspaceButton size="md" variant="primary">
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    Add requirements
                    <ChevronDown className="h-3 w-3" aria-hidden="true" />
                  </WorkspaceButton>
                }
              />
            </>
          ) : null
        }
      />
      <SelectionTabs
        active="requirements"
        requirementsCount={summary.total}
        showVendorIntelligence={
          engagementQuery.data?.modules_enabled.includes('selection') === true
        }
        onSelect={onSelectTab}
      />
    </>
  );

  const picker = pickerOpen ? (
    tree && itemsQuery.data ? (
      <LibraryPickerSheet
        engagementId={engagementId}
        engagementLabel={engagementLabel}
        tree={tree}
        items={itemsQuery.data.items}
        rows={allRows}
        onClose={() => setPickerOpen(false)}
        onLoaded={onLoaded}
        onStale={() =>
          void qc.invalidateQueries({ queryKey: engagementRequirementKeys.list(engagementId) })
        }
      />
    ) : (
      <Sheet open onOpenChange={(next) => !next && setPickerOpen(false)} title="Add from library">
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-body-sm text-ink-500">
          {treeQuery.error || itemsQuery.error ? (
            <>
              <ErrorMessage error={treeQuery.error ?? itemsQuery.error} />
              <WorkspaceButton
                onClick={() => {
                  void treeQuery.refetch();
                  void itemsQuery.refetch();
                }}
              >
                Try again
              </WorkspaceButton>
            </>
          ) : (
            <span className="flex items-center gap-2">
              <Spinner /> Loading the requirement library…
            </span>
          )}
        </div>
      </Sheet>
    )
  ) : null;

  const importing = (
    <>
      {matchOpen ? (
        <LibraryMatchSheet
          engagementId={engagementId}
          engagementLabel={engagementLabel}
          modules={manualModules}
          onClose={() => setMatchOpen(false)}
          onShowLoad={(loadId) => {
            setMatchOpen(false);
            setSelected(new Set());
            if (onLoadChange) onLoadChange(loadId);
            else onClearFilters();
          }}
        />
      ) : null}
      {importOpen ? (
        <EngagementImportSheet
          engagementId={engagementId}
          engagementLabel={engagementLabel}
          modules={manualModules}
          onClose={() => setImportOpen(false)}
          onImported={onImported}
        />
      ) : null}
      <ImportHistoryDialog
        engagementId={engagementId}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        undoingId={undoImportMutation.isPending ? (undoImportMutation.variables ?? null) : null}
        onUndo={(item) => undoImport(item.load_id)}
      />
    </>
  );

  const manualPanel =
    panel?.kind === 'new' ? (
      <ManualAddPanel
        modules={manualModules}
        defaultModuleId={
          view && !removedView && view !== NOT_MAPPED_VIEW
            ? view
            : (v.live.find((r) => r.module_id)?.module_id ?? null)
        }
        documents={documents}
        saving={createMutation.isPending}
        error={manualError}
        duplicate={manualDuplicate}
        onSubmit={addManually}
        onClose={() => setPanel(null)}
      />
    ) : null;

  // ---- empty engagement ---------------------------------------------------
  if (!hasAny && panel?.kind !== 'new') {
    return (
      <>
        <div className="flex h-full min-h-0 flex-col">
          {header}
          <div className="flex flex-1 flex-col items-center justify-center gap-6 border-t border-ink-100 bg-paper-0 px-6 py-10">
            <div className="text-center">
              <h2 className="text-h2 font-semibold text-ink-900">
                Start this engagement&rsquo;s requirements
              </h2>
              <p className="mx-auto mt-1 max-w-[520px] text-body text-ink-500">
                Bring in the firm&rsquo;s standard requirements, write your own, or both. Nothing is
                final: every requirement can be reworded, re-prioritised or removed afterwards.
              </p>
            </div>
            <div
              className={`grid grid-cols-1 gap-4 ${fromDiscovery ? 'md:grid-cols-3' : 'md:grid-cols-2 xl:grid-cols-4'}`}
            >
              <StartCard
                primary
                icon={<BookOpen className="h-[18px] w-[18px]" aria-hidden="true" />}
                title="Add from library"
                onClick={openPicker}
              >
                Pick modules, sub-categories or single requirements from the firm&rsquo;s standard
                requirements
                {tree ? ` (${tree.item_count.toLocaleString()})` : ''}. You set criticality for this
                client afterwards.
              </StartCard>
              <StartCard
                icon={<PenLine className="h-[18px] w-[18px]" aria-hidden="true" />}
                title="Write one manually"
                onClick={openManual}
              >
                Write a requirement yourself and say why it is being added. You can mix both at any
                time.
              </StartCard>
              <StartCard
                icon={<Upload className="h-[18px] w-[18px]" aria-hidden="true" />}
                title="Import a spreadsheet"
                onClick={openImport}
              >
                Bring in the client&rsquo;s own .xlsx or .csv list. Sheets, header rows and columns
                are worked out for you; you review before anything is saved.
              </StartCard>
              <StartCard
                icon={<GitCompare className="h-[18px] w-[18px]" aria-hidden="true" />}
                title="Match a spreadsheet to the library"
                onClick={openMatch}
              >
                Finds the library item for each row of the client&rsquo;s list; what doesn&rsquo;t
                match waits for you. Nothing is added until you apply.
              </StartCard>
              {fromDiscovery ? (
                <StartCard
                  icon={<Lightbulb className="h-[18px] w-[18px]" aria-hidden="true" />}
                  title="Add from discovery"
                  onClick={fromDiscovery}
                >
                  Review requirements AI suggests from confirmed discovery evidence, each quoting
                  what people said. Nothing is added until you confirm.
                </StartCard>
              ) : null}
            </div>
          </div>
        </div>
        {picker}
        {importing}
      </>
    );
  }

  // ---- toolbar ------------------------------------------------------------
  const toolbar = (
    <>
      <SearchField
        ref={searchRef}
        value={query}
        onChange={onQueryChange}
        aria-label="Search this engagement's requirements"
        wrapperClassName="w-auto min-w-[180px] max-w-72 flex-1"
        placeholder="Search this engagement's requirements…"
        icon={<Search className="h-3.5 w-3.5 flex-none" aria-hidden="true" />}
        hint={<kbd className="kbd">/</kbd>}
      />
      <FilterChips
        aria-label="Filter by criticality"
        options={CRITICALITY_FILTERS}
        value={criticality}
        onChange={onCriticalityChange}
      />
      <span className="h-[18px] w-px bg-ink-100" aria-hidden="true" />
      <FilterChips
        aria-label="Filter by source"
        options={SOURCE_FILTERS}
        value={source}
        onChange={onSourceChange}
      />
      <span className="h-[18px] w-px bg-ink-100" aria-hidden="true" />
      <FilterChips
        aria-label="Group by"
        options={GROUP_BY_OPTIONS}
        value={grouping}
        onChange={changeGrouping}
      />
      {load && onLoadChange ? (
        <FilterChip on onClick={() => onLoadChange(null)}>
          Rows from one apply
          <X className="h-3 w-3" aria-label="Show all requirements" />
        </FilterChip>
      ) : null}
      <div className="ml-auto flex items-center gap-2">
        <span className="whitespace-nowrap text-meta text-ink-500" aria-live="polite">
          {rows.length.toLocaleString()} shown
        </span>
        <WorkspaceButton variant="tertiary" aria-pressed={wrap} onClick={() => setWrap((w) => !w)}>
          <WrapText className="h-3.5 w-3.5" aria-hidden="true" />
          {wrap ? 'Single line' : 'Wrap text'}
        </WorkspaceButton>
      </div>
    </>
  );

  // ---- main content -------------------------------------------------------
  const trimmedQuery = query.trim();
  let emptyState: React.ReactNode;
  if (!hasAny) {
    emptyState = (
      <EmptyInline title="No requirements yet">
        Fill in the form to add the first requirement.
      </EmptyInline>
    );
  } else if (v.narrowed) {
    emptyState = (
      <EmptyInline title="No requirements to show">
        {terms.length > 0
          ? `Nothing matches "${trimmedQuery}"${viewModuleName ? ` in ${viewModuleName}` : ''}.`
          : 'Nothing matches these filters.'}
        <div className="mt-3">
          <WorkspaceButton onClick={onClearFilters}>Clear search and filters</WorkspaceButton>
        </div>
      </EmptyInline>
    );
  } else {
    emptyState = (
      <EmptyInline
        title={
          removedView
            ? 'Nothing is removed'
            : view === NOT_MAPPED_VIEW
              ? 'Every requirement is mapped'
              : 'No requirements in this module'
        }
      >
        {removedView
          ? 'Removed requirements appear here and can be restored.'
          : view === NOT_MAPPED_VIEW
            ? 'Requirements with no library module appear here until they are mapped.'
            : 'This engagement has no active requirements in the selected module.'}
        <div className="mt-3">
          <WorkspaceButton onClick={() => selectView(null)}>Show all requirements</WorkspaceButton>
        </div>
      </EmptyInline>
    );
  }

  const selectedIds = Array.from(selected);
  const bulkActions = removedView
    ? [
        {
          id: 'restore',
          label: 'Restore',
          icon: <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />,
          disabled: busy,
          onClick: () => changeStatus(selectedIds, 'restore'),
        },
      ]
    : [
        {
          id: 'criticality',
          label: 'Set criticality',
          disabled: busy,
          menu: {
            label: `Set ${selected.size.toLocaleString()} to`,
            items: criticalityMenuItems((value) => bulkSetCriticality(selectedIds, value)),
          },
        },
        {
          id: 'remove',
          label: 'Remove',
          icon: <X className="h-3.5 w-3.5" aria-hidden="true" />,
          disabled: busy,
          onClick: () => changeStatus(selectedIds, 'remove'),
        },
      ];

  return (
    <>
      <WorkspaceLayout
        header={header}
        railLabel="Requirement modules"
        rail={
          <TreeRail
            aria-label="Modules in this engagement"
            all={{
              id: ALL_ID,
              label: 'All requirements',
              count: removedView ? v.live.length : v.base.length,
            }}
            categories={railTree}
            footerItems={
              hasNotMapped || v.removed.length > 0
                ? [
                    ...(hasNotMapped
                      ? [
                          {
                            id: NOT_MAPPED_VIEW,
                            label: NOT_MAPPED_LABEL,
                            count: notMappedLive,
                            icon: <CircleDashed className="h-3.5 w-3.5" />,
                          },
                        ]
                      : []),
                    ...(v.removed.length > 0
                      ? [
                          {
                            id: REMOVED_VIEW,
                            label: 'Removed',
                            count: v.removed.length,
                            icon: <Archive className="h-3.5 w-3.5" />,
                          },
                        ]
                      : []),
                  ]
                : undefined
            }
            activeId={view ?? ALL_ID}
            dimZero={v.narrowed}
            onSelect={(id) => selectView(id === ALL_ID ? null : id)}
          />
        }
        toolbar={toolbar}
        footer={
          <>
            <KeyHint keys={['j', 'k']}>move</KeyHint>
            <KeyHint keys={['Enter']}>open</KeyHint>
            <KeyHint keys={['x']}>select</KeyHint>
            {removedView ? null : <KeyHint keys={['e']}>edit text (or double-click)</KeyHint>}
            <KeyHint keys={['/']}>search</KeyHint>
            <KeyHint keys={['Esc']}>close panel</KeyHint>
          </>
        }
        overlay={
          <BulkBar
            count={selected.size}
            noun="requirement"
            selectAll={{
              total: rows.length,
              onSelectAll: () => setSelected(new Set(rows.map((r) => r.id))),
            }}
            actions={bulkActions}
            onClear={() => setSelected(new Set())}
          />
        }
        panel={
          manualPanel ??
          (panelRow ? (
            <RequirementPanel
              engagementId={engagementId}
              row={panelRow}
              libraryWording={libraryWordingDifference(panelRow, libraryById)}
              modules={manualModules}
              busy={busy}
              userName={userName}
              documentName={documentName}
              onClose={() => setPanel(null)}
              onCommitText={commitText}
              onSetCriticality={setCriticality}
              onSetClientArea={setClientArea}
              onSetModule={setModule}
              onRemove={(r) => changeStatus([r.id], 'remove')}
              onRestore={(r) => changeStatus([r.id], 'restore')}
            />
          ) : undefined)
        }
      >
        <WorkspaceDataGrid
          aria-label={
            removedView
              ? 'Removed requirements'
              : viewModuleName
                ? `${viewModuleName} requirements`
                : 'Engagement requirements'
          }
          rows={rows}
          getRowId={getRowId}
          columns={columns}
          groupBy={groupBy}
          renderGroup={renderGroup}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          focusedId={focusedId}
          onFocusChange={focusRow}
          onOpen={openRow}
          isRowEditable={isActiveRow}
          onEditCommit={onEditCommit}
          rowActions={rowActions}
          wrap={wrap}
          globalKeys
          emptyState={emptyState}
        />
      </WorkspaceLayout>
      {picker}
      {importing}
    </>
  );
}

function EmptyInline({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="px-6 py-14 text-center text-body-sm text-ink-500">
      <strong className="mb-1 block text-h3 font-semibold text-ink-900">{title}</strong>
      {children}
    </div>
  );
}

/** One way to start, on the empty engagement (prototype `.start-card`). */
function StartCard({
  icon,
  title,
  tag,
  primary = false,
  disabled = false,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  tag?: string;
  primary?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-[260px] flex-col gap-2 rounded-large border p-5 text-left transition-shadow',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-2',
        primary ? 'border-accent-amber-600 bg-accent-amber-50' : 'border-ink-100 bg-paper-0',
        disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-ink-300 hover:shadow-modal',
      )}
    >
      <span
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-card',
          primary ? 'bg-accent-amber-100 text-accent-amber-700' : 'bg-paper-100 text-ink-700',
        )}
      >
        {icon}
      </span>
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-h3 font-semibold text-ink-900">
        {title}
        {tag ? (
          <span className="inline-flex h-[22px] items-center whitespace-nowrap rounded-full border border-ink-100 bg-paper-100 px-2.5 text-meta font-medium text-ink-700">
            {tag}
          </span>
        ) : null}
      </span>
      <span className="text-body-sm text-ink-500">{children}</span>
    </button>
  );
}
